import React, { useState } from "react";
import { MessageCircle, X, Star, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCountry } from "../lib/country";
import { useAuth } from "../lib/auth";
import { useLanguage } from "../lib/language";
import { toast } from "sonner";

/**
 * Floating "Feedback" button + modal.
 * Anonymous by default. Captures country and current page automatically.
 */
export default function FeedbackWidget() {
    const { country } = useCountry();
    const { user } = useAuth();
    const { t } = useLanguage();
    const CATEGORIES = [
        { id: "general", label: t("General", "General") },
        { id: "elogio", label: t("Me encantó algo", "I loved something") },
        { id: "sugerencia", label: t("Tengo una sugerencia", "I have a suggestion") },
        { id: "bug", label: t("Encontré un error", "I found a bug") },
        { id: "reclamo", label: t("Reclamo", "Complaint") },
    ];
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [rating, setRating] = useState(0);
    const [category, setCategory] = useState("general");
    const [anonymous, setAnonymous] = useState(true);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);

    const reset = () => {
        setMessage(""); setRating(0); setCategory("general");
        setAnonymous(true); setName(""); setEmail("");
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!message.trim()) { toast.error("Cuéntanos tu experiencia"); return; }
        setSending(true);
        try {
            await api.post("/feedback", {
                message: message.trim(),
                rating: rating || null,
                category,
                anonymous,
                name: anonymous ? "" : (name || user?.name || ""),
                email: anonymous ? "" : (email || user?.email || ""),
                country,
                page: window.location.pathname,
            });
            toast.success("¡Gracias por tu feedback!");
            reset();
            setOpen(false);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
        } finally { setSending(false); }
    };

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="fixed bottom-4 right-4 z-[999999] inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[var(--tb-gold)] text-black font-semibold shadow-2xl ring-2 ring-black/20 hover:scale-105 transition text-sm"
                style={{ minWidth: "180px", justifyContent: "center" }}
                data-testid="feedback-fab"
                aria-label={t("Enviar opinión", "Send feedback")}>
                <MessageCircle className="w-4 h-4" />
                <span>{t("Enviar opinión", "Send feedback")}</span>
            </button>

            {open && (
                <div className="fixed inset-0 bg-black/70 z-[200] flex items-end sm:items-center justify-center p-3 overflow-y-auto" onClick={() => setOpen(false)} data-testid="feedback-modal">
                    <form
                        onClick={(e) => e.stopPropagation()}
                        onSubmit={submit}
                        className="tb-card w-full max-w-md p-6 space-y-4 my-2">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="font-display text-2xl">{t("Tu opinión cuenta", "Your feedback matters")}</h3>
                                <p className="text-xs text-[var(--tb-text-2)] mt-1">
                                    {t("Comparte cómo fue tu experiencia. Puedes ser anónimo.", "Share how your experience was. You can stay anonymous.")}
                                </p>
                            </div>
                            <button type="button" onClick={() => setOpen(false)} className="text-[var(--tb-muted)] hover:text-[var(--tb-text)]" aria-label={t("Cerrar", "Close")}>
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Stars */}
                        <div>
                            <div className="tb-overline mb-1">{t("Calificación general", "Overall rating")}</div>
                            <div className="flex gap-1" data-testid="feedback-stars">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <button key={n} type="button" onClick={() => setRating(rating === n ? 0 : n)}
                                        className="transition hover:scale-110"
                                        data-testid={`feedback-star-${n}`}
                                        aria-label={`${n} ${t("estrellas", "stars")}`}>
                                        <Star className={`w-7 h-7 ${n <= rating ? "text-[var(--tb-gold)] fill-[var(--tb-gold)]" : "text-[var(--tb-muted)]"}`} />
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Category */}
                        <div>
                            <label className="tb-overline">{t("Tipo", "Type")}</label>
                            <select className="tb-input mt-1" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="feedback-category">
                                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </div>

                        {/* Message */}
                        <div>
                            <label className="tb-overline">{t("Tu mensaje *", "Your message *")}</label>
                            <textarea
                                className="tb-input mt-1 min-h-[120px]"
                                placeholder={t("Cuéntanos qué te gustó, qué mejorarías o cualquier cosa que quieras compartir...", "Tell us what you liked, what you would improve, or anything you want to share...")}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                maxLength={4000}
                                data-testid="feedback-message"
                            />
                            <div className="text-[10px] text-right text-[var(--tb-muted)] mt-0.5">{message.length}/4000</div>
                        </div>

                        {/* Anonymity */}
                        <div>
                            <label className="flex items-start gap-2 cursor-pointer text-sm">
                                <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} className="mt-0.5" data-testid="feedback-anonymous" />
                                <span>
                                    {t("Enviar de forma anónima", "Send anonymously")}
                                    <span className="block text-[10px] text-[var(--tb-muted)]">
                                        {t("No guardaremos tu nombre, correo ni datos de cuenta.", "We won't store your name, email or account data.")}
                                    </span>
                                </span>
                            </label>
                            {!anonymous && (
                                <div className="grid grid-cols-2 gap-2 mt-3">
                                    <input className="tb-input !text-xs" placeholder={t("Nombre", "Name")} value={name || user?.name || ""}
                                        onChange={(e) => setName(e.target.value)} data-testid="feedback-name" />
                                    <input className="tb-input !text-xs" placeholder={t("Correo electrónico", "Email address")} type="email"
                                        value={email || user?.email || ""}
                                        onChange={(e) => setEmail(e.target.value)} data-testid="feedback-email" />
                                </div>
                            )}
                        </div>

                        <button type="submit" disabled={sending || !message.trim()}
                            className="tb-btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                            data-testid="feedback-submit">
                            <Send className="w-4 h-4" /> {sending ? t("Enviando...", "Sending...") : t("Enviar opinión", "Send feedback")}
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}
